import type { StatusCode } from "@frontierx/shared/types";

import { Button } from "@/components/ui/button";

import { useNetworkStatus } from "../NetworkStatus/NetworkStatusProvider";
import type { PartyServerConnectionStatus } from "./hooks/useCallDisplay";

export interface CallStatusProps {
  status?: StatusCode;
  handleRequestBotInfo: () => void;
  partyServerConnectionStatus: PartyServerConnectionStatus;
}

export function CallStatus({
  status,
  handleRequestBotInfo,
  partyServerConnectionStatus,
}: Readonly<CallStatusProps>) {
  const { isOnline } = useNetworkStatus();

  const formatStatus = (status: string): string => {
    if (status === "in_call_recording") {
      return "In Call Recording";
    }
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "starting_up":
        return "bg-gray-500";
      case "call_connected":
      case "in_call_recording":
      case "audio_unmuted":
      case "video_unmuted":
        return "bg-green-500";
      case "call_connecting":
      case "audio_muted":
      case "video_muted":
      case "in_call_not_recording":
      case "reconnecting":
        return "bg-orange-500";
      case "call_failed":
      case "error":
      case "call_disconnected":
      case "disconnected":
      case "offline":
        return "bg-red-500";
      case "ready":
        return "bg-gray-500";
      default:
        return "bg-gray-500";
    }
  };

  let displayStatus: string;
  let dotColor: string;

  if (!isOnline) {
    displayStatus = "Offline";
    dotColor = getStatusColor("offline");
  } else if (
    partyServerConnectionStatus === "disconnected" ||
    partyServerConnectionStatus === "reconnecting"
  ) {
    displayStatus = formatStatus(partyServerConnectionStatus);
    dotColor = getStatusColor(partyServerConnectionStatus);
  } else {
    displayStatus = status ? formatStatus(status) : "Waiting";
    dotColor = status ? getStatusColor(status) : "bg-gray-500";
  }

  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => handleRequestBotInfo()}
      data-test="call-status"
    >
      <div
        className={`h-2 w-2 rounded-full ${dotColor} mr-2`}
        data-test="call-status-indicator"
      />
      <span data-test="call-status-text">{displayStatus}</span>
    </Button>
  );
}
